import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  ColumnsButton,
  ColumnsDrawer,
  Confirmation,
  DateTime,
  DeleteConfirmation,
  ExportDialog,
  FiltersBar,
  Icon,
  IconButton,
  IconTooltip,
  InlineTooltip,
  MemberContactPopover,
  Page,
  SearchInput,
  Spinner,
} from '~/components';
import { Board, Card, CardMenu, List } from '~/components/board';
import { FilterButton, TaskFiltersBar } from '~/components/filters';
import { useApi, useConfirmation, useSubscription, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle, useSearchParams, useSearchParamsConfig, useViewColumns } from '~/hooks';
import projectTaskStatuses from '~/lookups/project-task-statuses';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, QueryString } from '~/utils';
import ProjectTaskCloneConfirmation from '../../project-tasks/ProjectTaskCloneConfirmation';
import ProjectTaskCreateForm from '../../project-tasks/ProjectTaskCreateForm';
import ProjectTaskDetails from '../../project-tasks/ProjectTaskDetails';
import ProjectTaskForm from '../../project-tasks/ProjectTaskForm';
import EditTimeEntry from '../../time/edit-time-entry';
import ExportTasksAction from './components/ExportTasksAction';
import NewTaskAction from './components/NewTaskAction';
import TaskFiltersDrawer from './components/TaskFiltersDrawer';
import TasksViewNav from './components/TasksViewNav';

const Styled = {
  Tags: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    font-size: 0.75rem;

    &:empty {
      display: none;
    }
  `,

  Tag: styled.div`
    border-radius: 0.3125rem;
    border-color: ${colors.grey10};
    padding: 0.25rem 0.5rem;
    display: flex;
    align-items: center;
    background-color: ${({ variant }) => colors[variant] || colors.primary};
    min-width: 0;
    position: relative;
  `,

  TagContents: styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,

  DragPreview: styled.div`
    display: flex;
    align-items: center;
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    margin-top: 0.75rem;
    margin-left: 0.75rem;
    border: 2px solid ${colors.primary};
    border-radius: 0.25rem;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    pointer-events: none;
    cursor: grab;
    min-width: 20rem;
  `,
};

const Tags = Styled.Tags;

function Tag({ children, ...props }) {
  return (
    <Styled.Tag {...props}>
      <Styled.TagContents>
        {children}
        <InlineTooltip message={children} delay={500} />
      </Styled.TagContents>
    </Styled.Tag>
  );
}

export default function TasksBoard({ project, onViewChange, setRefetch }) {
  const documentTitle = useDocumentTitle();
  const [dialog, setDialog] = useState(null);

  const lists = [projectTaskStatuses.not_started, projectTaskStatuses.in_progress, projectTaskStatuses.completed];

  const api = useApi();
  const { workspace } = useWorkspace();

  const toast = useToast();

  const [{ tasks, isLoading }, setTasks] = useState({ tasks: null, isLoading: true });

  const [params, setParams] = useState({
    q: '',
    projectTaskRecordStatusId: '',
    projectTaskStatuses: [],
    projectTaskTags: [],
  });

  const searchParamsConfig = useSearchParamsConfig();
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        projectTaskStatuses: searchParamsConfig.projectTaskStatuses,
        projectTaskRecordStatusId: { default: 'active', ...searchParamsConfig.projectTaskRecordStatusId },
        projectTaskTags: searchParamsConfig.projectTaskTags,
      }),
      [searchParamsConfig],
    ),
    sessionKey: 'project_dashboard_tasks_board',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      if (params) {
        setParams((state) => ({ ...state, ...params }));
        setSearchParamsStatus('ready');
      }
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(
    async (p = params) => {
      const urlSearchParams = {
        q: p.q,
        projectTaskStatusId: p.projectTaskStatuses?.map((v) => v.id),
        projectTaskRecordStatusId: p.projectTaskRecordStatusId ?? undefined,
        projectTaskTagId: p.projectTaskTags?.map((v) => v.id),
      };

      try {
        setTasks((state) => ({ ...state, isLoading: true }));
        const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks().board.get(urlSearchParams);
        setTasks({ tasks: data, isLoading: false });
        return data;
      } catch (error) {
        setTasks({ tasks: [], isLoading: false });
        toast.error(error.message);
      }
    },
    [api, project.id, workspace.id, params, toast],
  );

  const handleFilter = async (filter) => {
    const newFilters = { ...params, ...filter };
    setParams(newFilters);
    searchParams.set(newFilters);

    const data = await fetchData(newFilters);
    if (!data) return;
  };

  const handleShowFiltersDrawer = () => {
    setDialog(<TaskFiltersDrawer values={params} onApply={handleFilter} onClose={() => setDialog(null)} />);
  };

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    if (tasks) return;
    fetchData();
  }, [fetchData, searchParamsStatus, tasks]);

  useEffect(() => {
    if (setRefetch) {
      setRefetch(() => fetchData);
    }
  }, [setRefetch, fetchData]);

  const handleCloseDialog = () => {
    documentTitle.set(project.name);
    setDialog(null);
  };

  const handleEdit = (task) => {
    setDialog(
      <ProjectTaskForm
        taskId={task.id}
        project={project}
        onClose={handleCloseDialog}
        onSaved={async () => {
          await fetchData();
          handleCloseDialog();
        }}
        onDelete={async () => {
          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this task?</DeleteConfirmation>
          ));
          if (!confirm) {
            return;
          }
          try {
            await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).delete();
            await fetchData();
            handleCloseDialog();
          } catch ({ message }) {
            toast.error(message);
          }
        }}
      />,
    );
  };

  const handleView = (task) => {
    setDialog(<ProjectTaskDetails taskId={task.id} project={project} onClose={handleCloseDialog} />);
  };

  const handleAddFromList = (statusId) => {
    setDialog(
      <ProjectTaskCreateForm
        project={project}
        defaultValues={{ statusId }}
        onClose={handleCloseDialog}
        onSaved={async (data, action) => {
          await fetchData();
          if (action === 'save') {
            handleEdit(data);
          }
        }}
      />,
    );
  };

  const confirmation = useConfirmation();
  const handleExport = async (filename, mimeType) => {
    const urlSearchParams = {
      projectId: project.id,
      q: params.q,
      projectTaskStatusId: params.projectTaskStatuses?.map((v) => v.id),
      projectTaskRecordStatusId: params.projectTaskRecordStatusId ?? undefined,
      projectTaskTagId: params.projectTaskTags?.map((v) => v.id),
    };

    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .projects(project.id)
          .tasks()
          .getExport(urlSearchParams, {
            headers: { accept: mimeType },
            responseType: 'blob',
          })}
        onClose={resolve}
      />
    ));
  };

  const handleMove = useCallback(
    async (card) => {
      const task = {
        ...tasks.find((t) => t.id === card.id),
        statusId: card.listId,
        status: projectTaskStatuses[card.listId],
        boardPosition: card.position,
      };

      setTasks((state) => ({ ...state, tasks: state.tasks.map((t) => (t.id === task.id ? task : t)) }));

      try {
        await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).board.update({
          statusId: card.listId,
          insertBeforeId: card.insertBeforeId,
          insertAfterId: card.insertAfterId,
        });
      } catch (error) {
        toast.error(error.message);
      }

      await fetchData();
    },
    [tasks, api, workspace.id, project.id, fetchData, toast],
  );

  const { notify } = useSubscription();
  const handleArchive = async (task) => {
    const confirm = await confirmation.prompt((resolve) => (
      <Confirmation resolve={resolve}>Are you sure that you want to archive this project task?</Confirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(task.id)
        .setRecordStatus({ recordStatusId: 'archived' });
      notify(useSubscription.keys.refresh_timer);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  };

  const handleUnarchive = async (task) => {
    const confirm = await confirmation.prompt((resolve) => (
      <Confirmation resolve={resolve}>Are you sure that you want to unarchive this project task?</Confirmation>
    ));

    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .projects(project.id)
        .tasks(task.id)
        .setRecordStatus({ recordStatusId: 'active' });
      notify(useSubscription.keys.refresh_timer);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  };

  async function handleDelete(task) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this task?</DeleteConfirmation>
    ));
    if (!confirm) {
      return;
    }
    try {
      await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleClone(task) {
    setDialog(
      <ProjectTaskCloneConfirmation
        target={{ id: task.id, name: task.name, projectId: project.id }}
        onClose={handleCloseDialog}
        onSaved={async () => {
          await fetchData();
          handleCloseDialog();
        }}
      />,
    );
  }

  async function handleTrackTime(task) {
    setDialog(
      <EditTimeEntry
        initialValues={{
          date: moment().format(dateFormats.isoDate),
          projectId: project.id,
          projectTaskId: task.id,
        }}
        onSaved={async () => {
          notify(useSubscription.keys.refresh_timer);
          await fetchData();
          handleCloseDialog();
        }}
        onClose={() => {
          handleCloseDialog();
          documentTitle.set(project.name);
        }}
      />,
    );
  }

  const cards = useMemo(() => {
    if (!tasks) return null;
    return _(tasks)
      .orderBy('boardPosition')
      .map((task) => ({ id: task.id, listId: task.statusId, position: task.boardPosition, task }))
      .value();
  }, [tasks]);

  const contentRef = useRef();
  useEffect(() => {
    if (!contentRef.current) return;
    const { top } = contentRef.current.getBoundingClientRect();
    contentRef.current.style.height = `calc(100vh - ${top}px - 2rem)`;
  }, [contentRef]);

  const columns = useViewColumns({
    storageKey: 'project_dashboard_tasks_board_columns',
    columns: [
      { key: 'name', name: 'Name', disabled: true },
      { key: 'code', name: 'Code', default: true },
      { key: 'start', name: 'Start Date', default: true },
      { key: 'end', name: 'End Date', default: true },
      { key: 'parent', name: 'Parent Task', default: true },
      { key: 'subtasks', name: 'Subtasks', default: true },
      { key: 'members', name: 'Assigned Members', default: true },
      { key: 'roles', name: 'Assigned Roles', default: true },
      { key: 'tags', name: 'Tags', default: true },
    ],
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  if (!cards) return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Tasks</Page.Title>

        <TasksViewNav value="board" onChange={(view) => onViewChange(view)} />

        <Page.Actions>
          <ExportTasksAction
            projectKey={project.key}
            onExport={(filename, mimeType) => handleExport(filename, mimeType)}
          />

          <ColumnsButton onClick={handleShowColumnsDrawer} />

          <FilterButton onClick={() => handleShowFiltersDrawer()} />

          <NewTaskAction
            project={project}
            onSaved={async (data, action) => {
              await fetchData();
              if (action === 'save') {
                handleEdit(data);
              }
            }}
          />
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SearchInput
            value={params.q}
            placeholder="Search"
            onChange={({ target: { value } }) => handleFilter({ q: value })}
          />

          <TaskFiltersBar
            filters={params}
            sticky={['projectTaskStatuses', 'projectTaskRecordStatusId', 'projectTaskTags']}
            onChange={handleFilter}
          />
        </FiltersBar>
      </Page.Filters>

      <Page.Section style={{ position: 'relative' }}>
        {isLoading && (
          <div style={{ position: 'absolute', fontSize: '0.75rem', bottom: 0, right: 0 }}>
            <Spinner style={{ fontSize: '0.75rem' }} />
          </div>
        )}

        <div ref={contentRef}>
          <Board cards={cards} onMove={handleMove}>
            {lists.map((list) => (
              <List key={list.id} id={list.id}>
                <List.Header>
                  <List.Title>{list.name}</List.Title>
                  <IconButton
                    disabled={!project.permissions.edit}
                    tooltip={
                      project.permissions.edit ? 'New Task' : 'Your security role prohibits you from creating tasks.'
                    }
                    icon="add"
                    onClick={() => handleAddFromList(list.id)}
                  />
                </List.Header>

                <List.Content>
                  {_(cards)
                    .filter({ listId: list.id })
                    .map(({ task }) => {
                      return (
                        <Card
                          isDraggable={task.permissions.manage}
                          key={task.id}
                          id={task.id}
                          listId={list.id}
                          renderDragPreview={() => <Styled.DragPreview>{task.name}</Styled.DragPreview>}>
                          <Card.Title onClick={() => (task.permissions.manage ? handleEdit(task) : handleView(task))}>
                            <IconTooltip
                              message={task.isBillable ? 'Billable' : 'Non-billable'}
                              icon="dollar-sign"
                              style={{ fontSize: '0.75rem', marginRight: '0.25rem', marginTop: '0.125rem' }}
                              color={task.isBillable ? colors.green : colors.grey25}
                              delay={150}
                            />

                            {task.name}
                          </Card.Title>

                          {columns.visible.code && task.code && (
                            <small style={{ fontWeight: weights.light }}>
                              <Icon icon="hashtag" spaceRight color={colors.grey40} />
                              {task.code}
                            </small>
                          )}

                          {columns.visible.parent && task.parent && (
                            <small style={{ fontWeight: weights.light }}>
                              <Icon icon="diagram-nested" spaceRight color={colors.grey40} />
                              {task.parent.name}
                            </small>
                          )}

                          {task.recordStatusId === 'archived' && (
                            <small style={{ fontWeight: weights.light }}>
                              <Icon icon="box-archive" spaceRight color={colors.grey40} />
                              Archived
                            </small>
                          )}

                          {columns.visible.start && task.start && (
                            <small>
                              <Icon icon="calendar-plus" spaceRight color={colors.grey40} />
                              <DateTime value={task.start} format={dateFormats.compactDate} />
                            </small>
                          )}

                          {columns.visible.end && task.end && (
                            <small>
                              <Icon icon="calendar-check" spaceRight color={colors.grey40} />
                              <DateTime value={task.end} format={dateFormats.compactDate} />
                            </small>
                          )}

                          {columns.visible.tags && task.tags.length > 0 && (
                            <Tags style={{ fontSize: '0.75rem' }}>
                              {task.tags.map((tag) => (
                                <Tag key={tag.id} variant="primary10">
                                  <Icon icon="tag" spaceRight color={colors.primary} />
                                  {tag.name}
                                </Tag>
                              ))}
                            </Tags>
                          )}

                          {columns.visible.roles && task.roles.length > 0 && (
                            <Tags>
                              {task.roles.map(({ role }) => (
                                <Tag key={role.id} variant="grey5">
                                  <Icon icon="user-tag" spaceRight color={colors.grey40} />
                                  {role.name}
                                </Tag>
                              ))}
                            </Tags>
                          )}

                          {columns.visible.subtasks && task.children?.length > 0 && (
                            <Tags>
                              {task.children.map((child) => (
                                <Tag key={child.id} variant="warning10">
                                  <Icon icon="diagram-subtask" spaceRight color={colors.warning} />
                                  {child.name}
                                </Tag>
                              ))}
                            </Tags>
                          )}

                          {columns.visible.members && task.members.length > 0 && (
                            <Tags>
                              {task.members.map(({ projectMember: { member } }) => (
                                <MemberContactPopover key={member.id} member={member} delay={300}>
                                  <Avatar value={member} isCircle hasBackground fontSize="0.75rem" size="1.5rem" />
                                </MemberContactPopover>
                              ))}
                            </Tags>
                          )}

                          <CardMenu>
                            {({ setIsOpen }) => (
                              <>
                                <CardMenu.Item onClick={() => setIsOpen(false) || handleView(task)}>View</CardMenu.Item>

                                <CardMenu.Item
                                  disabled={!task.permissions.manage}
                                  tooltip={
                                    !task.permissions.manage
                                      ? "Insufficient permissions to modify this project's tasks."
                                      : undefined
                                  }
                                  onClick={() => setIsOpen(false) || handleEdit(task)}>
                                  Edit
                                </CardMenu.Item>

                                <CardMenu.Item
                                  disabled={!task.permissions.manage}
                                  onClick={() => setIsOpen(false) || handleClone(task)}>
                                  Clone
                                </CardMenu.Item>

                                {task.recordStatusId === 'active' ? (
                                  <CardMenu.Item
                                    disabled={!task.permissions.manage}
                                    tooltip={
                                      !task.permissions.manage
                                        ? 'Insufficient permissions to archive this project task.'
                                        : undefined
                                    }
                                    onClick={() => {
                                      setIsOpen(false);
                                      handleArchive(task);
                                    }}>
                                    Archive
                                  </CardMenu.Item>
                                ) : (
                                  <CardMenu.Item
                                    disabled={!task.permissions.manage}
                                    tooltip={
                                      !task.permissions.manage
                                        ? 'Insufficient permissions to unarchive this project task.'
                                        : undefined
                                    }
                                    onClick={() => {
                                      setIsOpen(false);
                                      handleUnarchive(task);
                                    }}>
                                    Unarchive
                                  </CardMenu.Item>
                                )}

                                <CardMenu.Link
                                  to={`/app/${workspace.key}/reports/time-entries?${new QueryString({
                                    start: 'not_set',
                                    end: 'not_set',
                                    project: project.id,
                                    projectTask: task.id,
                                  })}`}>
                                  View Time Entries
                                </CardMenu.Link>

                                <CardMenu.Item
                                  onClick={() => setIsOpen(false) || handleTrackTime(task)}
                                  disabled={
                                    !task.isProjectMember ||
                                    task.project.recordStatusId !== 'active' ||
                                    (task.forAssignedOnly ? !task.assignedToMe : false) ||
                                    task.lockTime ||
                                    project.lockTimeAndExpenses ||
                                    task.recordStatusId === 'archived'
                                  }
                                  tooltip={(() => {
                                    if (!task.isProjectMember)
                                      return "In order to track time to this task, you must be an active project team member of this task's project.";
                                    if (task.project.recordStatusId !== 'active') return 'This project is archived.';
                                    if (task.forAssignedOnly && !task.assignedToMe)
                                      return 'In order to track time to this task, you or your project role must be assigned to the task.';
                                    if (project.lockTimeAndExpenses)
                                      return "This task's project does not allow adding new time entries.";
                                    if (task.lockTime) return 'This task does not allow adding new time entries.';
                                    if (task.recordStatusId === 'archived')
                                      return 'New time entries may not be associated with an archived task.';
                                  })()}>
                                  Track Time
                                </CardMenu.Item>

                                <CardMenu.Item
                                  disabled={!task.permissions.manage}
                                  tooltip={
                                    !task.permissions.manage
                                      ? "Insufficient permissions to delete this project's tasks."
                                      : undefined
                                  }
                                  onMount={async (setState) => {
                                    setState((state) => ({ ...state, disabled: true }));
                                    if (task.permissions.manage) {
                                      const { data: hasDependencies } = await api.www
                                        .workspaces(workspace.id)
                                        .projects(project.id)
                                        .tasks(task.id)
                                        .hasDependencies();

                                      setState((state) => ({
                                        ...state,
                                        disabled: hasDependencies,
                                        tooltip: hasDependencies ? 'This task is currently in use.' : null,
                                      }));
                                    }
                                  }}
                                  onClick={() => setIsOpen(false) || handleDelete(task)}>
                                  Delete
                                </CardMenu.Item>
                              </>
                            )}
                          </CardMenu>
                        </Card>
                      );
                    })
                    .value()}
                </List.Content>
              </List>
            ))}
          </Board>
        </div>
      </Page.Section>

      {dialog}
    </>
  );
}
